/**
 * The widget's own copy, in the language of the page it is embedded in.
 *
 * Everything here is a DEFAULT. A client who sets `greeting` or
 * `defaultSuggestions` in their snippet (or, since 1.6.0, in the console —
 * see config.ts) still wins — this is what the widget says when it has not
 * been told, and it used to say it in English on every site in the world.
 *
 * That was not only untidy, it broke retrieval. The default chips read "What
 * services do you offer?" on Ecobel's Italian site, and the assistant searches
 * its corpus by embedding the question: measured against those 60 live Italian
 * pages, the English question admitted ZERO of them and the Italian one
 * admitted two. Clicking the widget's own suggestion was a guaranteed miss, and
 * the visitor was told the business had no information at all.
 *
 * Languages are the five the company can actually review. Anything else falls
 * back to English rather than to a machine translation nobody has read — and
 * the assistant still ANSWERS in the visitor's language, because that is the
 * backend's job (common/languages.py), not this table's.
 *
 * Templates: a few strings carry a `{name}` placeholder — fill them with
 * `fill()` rather than concatenating, so word order stays the translator's.
 */

export interface WidgetStrings {
  subtitle: string;
  greeting: string;
  suggestions: string[];
  errorMessage: string;
  originBlockedMessage: string;
  /**
   * Canned backend replies, keyed by the `code` the API sends beside them.
   * The server's `reply` text is English; a visitor reading Italian gets
   * these instead (shared-chatbot review S10).
   */
  noContent: string;
  overCap: string;
  unavailable: string;
  refused: string;
  /**
   * The info panel behind the header's ⓘ (1.19.0): what this assistant is,
   * how the conversation is handled, and the way to tell us about it. The
   * privacy line used to sit under the composer, where it cost a line of the
   * conversation on every screen; it is now one tap away and says the same
   * thing. `assistantVersion` takes `{version}` — the package version, so a
   * support conversation can start from what the visitor is actually running.
   */
  aboutAssistant: string;
  closeInfo: string;
  assistantVersion: string;
  privacyNotice: string;
  privacyLinkLabel: string;
  /**
   * The link label used when the business has set NO privacy URL of its own.
   *
   * It must not read as "the privacy policy", because the notice it points at
   * is PuglieseWeb's, not the business's: we process this conversation on the
   * business's behalf, and our notice says so and says what we do with it. A
   * visitor sent to the operator's notice under the words "Privacy Policy"
   * would reasonably think they were reading the policy of the company whose
   * website they are on. They are not.
   */
  privacyOperatorLinkLabel: string;
  typeAMessage: string;
  sendMessage: string;
  openChat: string;
  closeChat: string;
  chatWithUs: string;
  /**
   * The visible pill beside the closed launcher on a desktop viewport — the
   * invitation, in the visitor's words: they talk to the website, and it
   * answers. `launcherLabel` on the snippet overrides it; `false` hides it.
   */
  talkToSite: string;
  /** `{name}` = business name. Accessible name of the dialog. */
  chatDialogLabel: string;
  /** `{name}` = business name. Label above a human operator's reply. */
  teamLabel: string;
  typing: string;
  tryAgain: string;
  /** "Report this issue" beside Try again, and what it says after (1.33.0, statusReport). */
  reportIssue: string;
  reportSent: string;
  reportNotSent: string;
  statusPage: string;
  newChat: string;
  confirmNewChat: string;
  tapAgainToClear: string;
  tapAgainToClearShort: string;
  expandChat: string;
  collapseChat: string;
  dismissSuggestions: string;
  /**
   * The two standing calls to action (1.18.0), drawn in their own row below
   * the conversation rather than among the suggestions: they are what the
   * assistant is FOR, and a suggestion row is swept away by every reply.
   * `leaveDetails` is also the lead chip's label wherever it still appears.
   */
  bookAppointment: string;
  /**
   * That the visitor may write in any language, said in two places because
   * one of them is easy to miss: a line under the greeting, and the input's
   * own placeholder, which rotates through `typeAMessageIn`.
   */
  anyLanguage: string;
  /**
   * The placeholder, in the OTHER languages — each already saying "type a
   * message" in its own words. Never the page's own language: seeing your own
   * language offered back tells you nothing.
   */
  typeAMessageIn: string[];
  /**
   * The header's language control (1.32.0, ADR-128 Amendment 4).
   * `languageButton` is the control's accessible name — `{language}` is the
   * language in force, in its own name. `languageAutomaticHint` explains
   * Automatic under its label. `languageChosen` is the line drawn in the
   * conversation when the visitor chooses THIS language, and so names this
   * language in itself; `languageAutomaticOn` the line when they go back to
   * Automatic. `{name}` is the assistant's name.
   */
  languageButton: string;
  languageMenuTitle: string;
  languageAutomatic: string;
  languageAutomaticHint: string;
  languageChosen: string;
  languageAutomaticOn: string;
  /** Lead capture (POST /chat/lead). */
  leaveDetails: string;
  leadTitle: string;
  leadSubmit: string;
  leadSending: string;
  leadCancel: string;
  /** `{name}` = the FIRST name the visitor typed. */
  leadThanks: string;
  leadFirstNameRequired: string;
  leadLastNameRequired: string;
  leadEmailInvalid: string;
  leadTooLong: string;
  leadError: string;
  leadCap: string;
  /** The form a business sets (ADR-114 Am.2). A required phone says `bookingPhoneRequired`. */
  leadMessageLabel: string;
  leadMessageRequired: string;
  leadFieldRequired: string;
  /** The empty first choice of a dropdown question. */
  leadChoose: string;
  /**
   * The ONE details form's fields (DetailsForm.tsx, ADR-110 Am.6 and Am.8),
   * the same whether the visitor leaves their details or books. The name is
   * two boxes and one stored value (details.ts). A required field is
   * its label and a visual `*`; an optional one says so in words with
   * `detailsOptional`, where `{label}` is the field's label — for a question
   * the business added, its own words as typed.
   */
  detailsFirstName: string;
  detailsLastName: string;
  detailsEmail: string;
  detailsPhone: string;
  detailsOptional: string;
  /**
   * Booking in the conversation (ADR-110). The details form's Cancel,
   * Sending… and validation copy is the lead's (leadCancel, leadSending,
   * leadFirstNameRequired, leadEmailInvalid, leadTooLong).
   */
  bookingDetailsTitle: string;
  bookingContinue: string;
  bookingPhoneRequired: string;
  /** The phone box holds something that is not written like a phone number (1.29.0). */
  leadPhoneInvalid: string;
  /** The backend refused the details and did not say which box (1.29.0). */
  leadInvalid: string;
  /**
   * The field rules (1.31.0, shared-chatbot#577 — shared-audit#17's catalogue):
   * one sentence per failure, the same rules chat_lead applies. `{label}` is
   * the box's own label; `{max}` / `{count}` numbers; a phone sentence names
   * the country (`{country}`, the browser's own name for it), its code
   * (`{dial}`) and how many digits follow it (`{digits}`: "10", "7–10").
   * `detailsCountry` names the country list beside the phone box.
   */
  detailsCountry: string;
  nameTooLong: string;
  nameCharacters: string;
  nameNeedsLetter: string;
  nameWebAddress: string;
  emailRequired: string;
  emailNoAt: string;
  emailSpaces: string;
  emailDomain: string;
  emailTooLong: string;
  phoneTooLong: string;
  phoneTooShort: string;
  /** `{otherDial}` / `{other}`: the code the number started with, and its country. */
  phoneWrongCountry: string;
  phoneUnknownCode: string;
  phoneNotValid: string;
  /** `{startsWith}`: digits no number in {country} starts with right after {dial} (a 00 after +47). */
  phoneStartsWith: string;
  /** A box past its limit, shown while typing: "{label} can be up to {max} characters — this is {count}." */
  textTooLong: string;
  /** A required dropdown question left on its empty choice. */
  selectRequired: string;
  /** The server refused a question this page never drew: the business changed its form since the page loaded. */
  leadFormChanged: string;
  /**
   * `{name}` = the FIRST name the visitor typed. Their bubble, and the message
   * sent, after the details form — it must never carry the email.
   */
  bookingUnderName: string;
  /** The same line when the form asked for no name. */
  bookingDetailsSent: string;
  /** The confirm buttons. What is SENT is always `yes` / `no`, in English. */
  bookingConfirmYes: string;
  bookingConfirmNo: string;
  /** The title of the card a finished booking shows. */
  bookingBooked: string;
  /** `{id}` = the booking reference. */
  bookingReference: string;
  /**
   * A booking the visitor already has (ADR-110 Amendment 10). The
   * `find_booking` form: its title, its two boxes, their refusals, its
   * button, and the visitor's own bubble after it — which carries the
   * reference and NEVER the email or phone. The form reuses leadCancel and
   * leadSending.
   */
  findBookingTitle: string;
  findBookingReference: string;
  findBookingContact: string;
  findBookingReferenceInvalid: string;
  findBookingContactInvalid: string;
  findBookingSubmit: string;
  /** `{id}` = the reference the visitor typed. */
  findBookingLine: string;
  /** The card a found booking shows, and the words for its state. */
  bookingFound: string;
  bookingCancelled: string;
  bookingChanged: string;
  /** `{when}` = the time a moved booking had before. */
  bookingWas: string;
  /** The confirm buttons under "shall I cancel it?" and "shall I change it?". */
  bookingConfirmCancelYes: string;
  bookingConfirmChangeYes: string;
  bookingConfirmKeep: string;
  /**
   * Feedback about the assistant (feedback.ts), filed with the Evrella
   * team. The form reuses the lead form's Cancel, Sending…, invalid-email and
   * too-long copy. `feedbackIntro` is the first thing the form says and must
   * keep naming WHO reads it: it is the visitor's notice that this does not
   * go to the business whose site they are on.
   */
  sendFeedback: string;
  backToChat: string;
  feedbackSubtitle: string;
  feedbackIntro: string;
  feedbackTypeLabel: string;
  feedbackTypeGeneral: string;
  feedbackTypeFeature: string;
  feedbackTypeBug: string;
  feedbackTypeLove: string;
  feedbackMessage: string;
  feedbackPlaceholder: string;
  feedbackEmail: string;
  feedbackSubmit: string;
  feedbackMessageRequired: string;
  feedbackThanks: string;
  feedbackError: string;
  /**
   * The human check (ADR-123). `humanCheckTitle` names the frame for assistive
   * tech while Cloudflare's challenge is on screen; `humanCheckFailed` is the
   * one sentence for every way a check can fail — the reason goes to the
   * console, where whoever runs the site can act on it.
   */
  humanCheckTitle: string;
  humanCheckFailed: string;
}

const en: WidgetStrings = {
  subtitle: 'AI Assistant',
  greeting: 'Hi! How can I help you today?',
  suggestions: ['What services do you offer?', 'Show me pricing', 'Tell me more'],
  errorMessage: "Sorry, I'm having trouble connecting. Please try again.",
  originBlockedMessage: "This assistant isn't available on this site.",
  noContent: "I don't have any information to answer from just yet. Please check back soon.",
  overCap: 'This assistant has reached its usage limit for now. Please try again later.',
  unavailable: "Sorry, the assistant isn't available right now.",
  refused: "Sorry, I can't help with that request.",
  aboutAssistant: 'About this assistant',
  closeInfo: 'Close',
  assistantVersion: 'Evrella Assistant {version}',
  privacyNotice: 'Messages are stored to provide this service.',
  privacyLinkLabel: 'Privacy Policy',
  privacyOperatorLinkLabel: 'How your messages are handled',
  typeAMessage: 'Type a message...',
  sendMessage: 'Send message',
  openChat: 'Open chat',
  closeChat: 'Close chat',
  chatWithUs: 'Chat with us',
  talkToSite: 'Talk to our website',
  chatDialogLabel: '{name} chat',
  teamLabel: '{name} team',
  typing: 'The assistant is typing',
  tryAgain: 'Try again',
  reportIssue: 'Report this issue',
  reportSent: 'Thanks, we have logged it.',
  reportNotSent: 'The report did not go through.',
  statusPage: 'System status',
  newChat: 'New chat',
  confirmNewChat: 'Confirm new chat — this clears the conversation',
  tapAgainToClear: 'Tap again to clear this conversation',
  tapAgainToClearShort: 'Tap again to clear',
  expandChat: 'Expand chat',
  collapseChat: 'Collapse chat',
  dismissSuggestions: 'Dismiss suggestions',
  bookAppointment: 'Book an appointment',
  anyLanguage: 'Write in any language — I answer in yours.',
  typeAMessageIn: ['Scrivi un messaggio…', 'Écrivez un message…', 'Schreiben Sie eine Nachricht…', 'Escribe un mensaje…'],
  languageButton: 'Language: {language}. Change language',
  languageMenuTitle: 'Reply language',
  languageAutomatic: 'Automatic',
  languageAutomaticHint: 'Follows the language you write in',
  languageChosen: '{name} will reply in English.',
  languageAutomaticOn: '{name} will reply in the language you write in.',
  leaveDetails: 'Leave your details',
  leadTitle: "Leave your details and we'll get back to you.",
  leadSubmit: 'Send',
  leadSending: 'Sending…',
  leadCancel: 'Cancel',
  leadThanks: "Thanks {name}! We've received your details and will be in touch soon.",
  leadFirstNameRequired: 'Please enter your first name.',
  leadLastNameRequired: 'Please enter your last name.',
  leadEmailInvalid: 'Please enter a valid email address.',
  leadTooLong: "That's a bit long — please shorten it.",
  leadError: "Sorry, we couldn't send your details. Please try again.",
  leadCap: "We're receiving a lot of enquiries right now. Please try again later.",
  leadMessageLabel: 'Message',
  leadMessageRequired: 'Please enter a message.',
  detailsOptional: '{label} (optional)',
  leadFieldRequired: 'Please fill in “{label}”.',
  leadChoose: 'Choose…',
  bookingDetailsTitle: 'Your details for the booking',
  detailsFirstName: 'First name',
  detailsLastName: 'Last name',
  detailsEmail: 'Email',
  detailsPhone: 'Phone',
  bookingContinue: 'Continue',
  bookingPhoneRequired: 'Please enter your phone number.',
  leadPhoneInvalid: 'Please enter a valid phone number.',
  leadInvalid: 'Please check your details and try again.',
  detailsCountry: 'Country',
  nameTooLong: '{label} must be {max} characters or fewer.',
  nameCharacters: '{label} can only include letters, spaces, hyphens, apostrophes and full stops.',
  nameNeedsLetter: '{label} must include at least one letter.',
  nameWebAddress: "{label} can't be a web address.",
  emailRequired: 'Enter your email address.',
  emailNoAt: 'An email address needs an @, like name@example.com.',
  emailSpaces: "Email addresses can't contain spaces.",
  emailDomain: 'Check the part after the @ — it should look like example.com.',
  emailTooLong: 'Email address must be {max} characters or fewer.',
  phoneTooLong: 'That number is too long for {country} — numbers there have {digits} digits after {dial}.',
  phoneTooShort: 'That number is too short for {country} — numbers there have {digits} digits after {dial}.',
  phoneWrongCountry: 'That number starts with {otherDial}, which is {other}. Choose {other} as the country, or enter a number for {country}.',
  phoneUnknownCode: "That number starts with {otherDial}, which isn't a country code.",
  phoneNotValid: "That isn't a number in {country} — the area code and the exchange each start with 2 to 9.",
  phoneStartsWith: "That isn't a number in {country} — no number there starts with {startsWith} after {dial}.",
  textTooLong: '{label} can be up to {max} characters — this is {count}.',
  selectRequired: 'Select “{label}”.',
  leadFormChanged: 'This form has changed since the page loaded — please reload the page and try again.',
  bookingUnderName: 'Booking under {name}',
  bookingDetailsSent: 'Here are my details',
  bookingConfirmYes: 'Yes, book it',
  bookingConfirmNo: 'No',
  bookingBooked: 'Booked',
  bookingReference: 'Reference {id}',
  findBookingTitle: 'Find your booking',
  findBookingReference: 'Booking reference',
  findBookingContact: 'Email or phone you used to book',
  findBookingReferenceInvalid: 'Please enter the reference from your confirmation email.',
  findBookingContactInvalid: 'Please enter a valid email address or phone number.',
  findBookingSubmit: 'Find booking',
  findBookingLine: 'Booking reference {id}',
  bookingFound: 'Your booking',
  bookingCancelled: 'Booking cancelled',
  bookingChanged: 'Booking changed',
  bookingWas: 'Was {when}',
  bookingConfirmCancelYes: 'Yes, cancel it',
  bookingConfirmChangeYes: 'Yes, change it',
  bookingConfirmKeep: 'No, keep it',
  sendFeedback: 'Send feedback',
  backToChat: 'Back to chat',
  feedbackSubtitle: 'Feedback about this assistant',
  feedbackIntro: 'Your feedback goes to Evrella, the team that makes this assistant.',
  feedbackTypeLabel: 'What is it about?',
  feedbackTypeGeneral: 'General',
  feedbackTypeFeature: 'Suggestion',
  feedbackTypeBug: 'Problem',
  feedbackTypeLove: 'Praise',
  feedbackMessage: 'Your feedback',
  feedbackPlaceholder: "What worked, what didn't, what would help?",
  feedbackEmail: 'Email (optional, if you would like a reply)',
  feedbackSubmit: 'Send feedback',
  feedbackMessageRequired: 'Please write your feedback first.',
  feedbackThanks: 'Thank you — your feedback has been sent.',
  feedbackError: "Sorry, we couldn't send your feedback. Please try again.",
  humanCheckTitle: 'Security check',
  humanCheckFailed: "Sorry, we couldn't confirm you're a person. Please try again.",
};

const it: WidgetStrings = {
  subtitle: 'Assistente AI',
  greeting: 'Ciao! Come posso aiutarti oggi?',
  suggestions: ['Quali servizi offrite?', 'Mostrami i prezzi', 'Dimmi di più'],
  errorMessage: 'Scusa, ho problemi di connessione. Riprova.',
  originBlockedMessage: 'Questo assistente non è disponibile su questo sito.',
  noContent: 'Non ho ancora informazioni per rispondere. Riprova tra poco.',
  overCap: 'Questo assistente ha raggiunto il limite di utilizzo per ora. Riprova più tardi.',
  unavailable: "Scusa, l'assistente non è disponibile in questo momento.",
  refused: 'Mi dispiace, non posso aiutarti con questa richiesta.',
  aboutAssistant: 'Informazioni sull’assistente',
  closeInfo: 'Chiudi',
  assistantVersion: 'Evrella Assistant {version}',
  privacyNotice: 'I messaggi vengono conservati per fornire questo servizio.',
  privacyLinkLabel: 'Informativa sulla privacy',
  privacyOperatorLinkLabel: 'Come vengono trattati i tuoi messaggi',
  typeAMessage: 'Scrivi un messaggio...',
  sendMessage: 'Invia il messaggio',
  openChat: 'Apri la chat',
  closeChat: 'Chiudi la chat',
  chatWithUs: 'Scrivici',
  talkToSite: 'Parla con il nostro sito',
  chatDialogLabel: 'Chat di {name}',
  teamLabel: 'Team {name}',
  typing: "L'assistente sta scrivendo",
  tryAgain: 'Riprova',
  reportIssue: 'Segnala il problema',
  reportSent: 'Grazie, l’abbiamo registrato.',
  reportNotSent: 'La segnalazione non è stata inviata.',
  statusPage: 'Stato del sistema',
  newChat: 'Nuova chat',
  confirmNewChat: 'Conferma la nuova chat — la conversazione verrà cancellata',
  tapAgainToClear: 'Tocca di nuovo per cancellare questa conversazione',
  tapAgainToClearShort: 'Tocca di nuovo per cancellare',
  expandChat: 'Espandi la chat',
  collapseChat: 'Riduci la chat',
  dismissSuggestions: 'Nascondi i suggerimenti',
  bookAppointment: 'Prenota un appuntamento',
  anyLanguage: 'Scrivi in qualsiasi lingua: ti rispondo nella tua.',
  typeAMessageIn: ['Type a message…', 'Écrivez un message…', 'Schreiben Sie eine Nachricht…', 'Escribe un mensaje…'],
  languageButton: 'Lingua: {language}. Cambia lingua',
  languageMenuTitle: 'Lingua delle risposte',
  languageAutomatic: 'Automatica',
  languageAutomaticHint: 'Segue la lingua in cui scrivi',
  languageChosen: '{name} risponderà in italiano.',
  languageAutomaticOn: '{name} risponderà nella lingua in cui scrivi.',
  leaveDetails: 'Lascia i tuoi dati',
  leadTitle: 'Lascia i tuoi dati e ti ricontatteremo.',
  leadSubmit: 'Invia',
  leadSending: 'Invio in corso…',
  leadCancel: 'Annulla',
  leadThanks: 'Grazie {name}! Abbiamo ricevuto i tuoi dati e ti ricontatteremo presto.',
  leadFirstNameRequired: 'Inserisci il tuo nome.',
  leadLastNameRequired: 'Inserisci il tuo cognome.',
  leadEmailInvalid: 'Inserisci un indirizzo email valido.',
  leadTooLong: 'Il testo è troppo lungo: accorcialo, per favore.',
  leadError: 'Scusa, non siamo riusciti a inviare i tuoi dati. Riprova.',
  leadCap: 'Stiamo ricevendo molte richieste in questo momento. Riprova più tardi.',
  leadMessageLabel: 'Messaggio',
  leadMessageRequired: 'Scrivi un messaggio.',
  detailsOptional: '{label} (facoltativo)',
  leadFieldRequired: 'Compila il campo “{label}”.',
  leadChoose: 'Scegli…',
  bookingDetailsTitle: 'I tuoi dati per la prenotazione',
  detailsFirstName: 'Nome',
  detailsLastName: 'Cognome',
  detailsEmail: 'Email',
  detailsPhone: 'Telefono',
  bookingContinue: 'Continua',
  bookingPhoneRequired: 'Inserisci il tuo numero di telefono.',
  leadPhoneInvalid: 'Inserisci un numero di telefono valido.',
  leadInvalid: 'Controlla i tuoi dati e riprova.',
  detailsCountry: 'Paese',
  nameTooLong: '{label}: massimo {max} caratteri.',
  nameCharacters: '{label} può contenere solo lettere, spazi, trattini, apostrofi e punti.',
  nameNeedsLetter: '{label} deve contenere almeno una lettera.',
  nameWebAddress: '{label} non può essere un indirizzo web.',
  emailRequired: 'Inserisci il tuo indirizzo email.',
  emailNoAt: 'Un indirizzo email deve contenere una @, come nome@esempio.it.',
  emailSpaces: 'Un indirizzo email non può contenere spazi.',
  emailDomain: 'Controlla la parte dopo la @: dovrebbe essere come esempio.it.',
  emailTooLong: "L'indirizzo email può avere al massimo {max} caratteri.",
  phoneTooLong: 'Il numero è troppo lungo per {country}: lì i numeri hanno {digits} cifre dopo {dial}.',
  phoneTooShort: 'Il numero è troppo corto per {country}: lì i numeri hanno {digits} cifre dopo {dial}.',
  phoneWrongCountry: 'Il numero inizia con {otherDial}, che è {other}. Scegli {other} come paese, oppure inserisci un numero di {country}.',
  phoneUnknownCode: 'Il numero inizia con {otherDial}, che non è un prefisso internazionale.',
  phoneNotValid: 'Non è un numero di {country}: il prefisso e il numero iniziano entrambi con una cifra da 2 a 9.',
  phoneStartsWith: 'Non è un numero di {country}: lì nessun numero inizia con {startsWith} dopo il prefisso {dial}.',
  textTooLong: '{label} può avere al massimo {max} caratteri: questo ne ha {count}.',
  selectRequired: 'Seleziona “{label}”.',
  leadFormChanged: 'Questo modulo è cambiato dopo il caricamento della pagina: ricarica la pagina e riprova.',
  bookingUnderName: 'Prenotazione a nome di {name}',
  bookingDetailsSent: 'Ecco i miei dati',
  bookingConfirmYes: 'Sì, prenota',
  bookingConfirmNo: 'No',
  bookingBooked: 'Prenotazione confermata',
  bookingReference: 'Riferimento {id}',
  findBookingTitle: 'Trova la tua prenotazione',
  findBookingReference: 'Codice prenotazione',
  findBookingContact: 'Email o telefono usati per prenotare',
  findBookingReferenceInvalid: 'Inserisci il codice che trovi nell’email di conferma.',
  findBookingContactInvalid: 'Inserisci un indirizzo email o un numero di telefono valido.',
  findBookingSubmit: 'Trova',
  findBookingLine: 'Codice prenotazione {id}',
  bookingFound: 'La tua prenotazione',
  bookingCancelled: 'Prenotazione annullata',
  bookingChanged: 'Prenotazione modificata',
  bookingWas: 'Prima: {when}',
  bookingConfirmCancelYes: 'Sì, annulla',
  bookingConfirmChangeYes: 'Sì, modifica',
  bookingConfirmKeep: 'No, lasciala',
  sendFeedback: 'Invia un feedback',
  backToChat: 'Torna alla chat',
  feedbackSubtitle: 'Feedback su questo assistente',
  feedbackIntro: 'Il tuo feedback arriva a Evrella, il team che realizza questo assistente.',
  feedbackTypeLabel: 'Di cosa si tratta?',
  feedbackTypeGeneral: 'Generale',
  feedbackTypeFeature: 'Suggerimento',
  feedbackTypeBug: 'Problema',
  feedbackTypeLove: 'Complimento',
  feedbackMessage: 'Il tuo feedback',
  feedbackPlaceholder: 'Cosa ha funzionato, cosa no, cosa ti sarebbe utile?',
  feedbackEmail: 'Email (facoltativa, se vuoi una risposta)',
  feedbackSubmit: 'Invia il feedback',
  feedbackMessageRequired: 'Scrivi prima il tuo feedback.',
  feedbackThanks: 'Grazie, il tuo feedback è stato inviato.',
  feedbackError: 'Scusa, non siamo riusciti a inviare il tuo feedback. Riprova.',
  humanCheckTitle: 'Controllo di sicurezza',
  humanCheckFailed: 'Scusa, non siamo riusciti a verificare che tu sia una persona. Riprova.',
};

const fr: WidgetStrings = {
  subtitle: 'Assistant IA',
  greeting: 'Bonjour ! Comment puis-je vous aider ?',
  suggestions: ['Quels services proposez-vous ?', 'Voir les tarifs', 'En savoir plus'],
  errorMessage: "Désolé, je n'arrive pas à me connecter. Veuillez réessayer.",
  originBlockedMessage: "Cet assistant n'est pas disponible sur ce site.",
  noContent: "Je n'ai pas encore d'informations pour répondre. Revenez bientôt.",
  overCap: "Cet assistant a atteint sa limite d'utilisation pour le moment. Veuillez réessayer plus tard.",
  unavailable: "Désolé, l'assistant n'est pas disponible pour le moment.",
  refused: 'Désolé, je ne peux pas vous aider avec cette demande.',
  aboutAssistant: 'À propos de cet assistant',
  closeInfo: 'Fermer',
  assistantVersion: 'Evrella Assistant {version}',
  privacyNotice: 'Les messages sont conservés pour fournir ce service.',
  privacyLinkLabel: 'Politique de confidentialité',
  privacyOperatorLinkLabel: 'Comment vos messages sont traités',
  typeAMessage: 'Écrivez un message...',
  sendMessage: 'Envoyer le message',
  openChat: 'Ouvrir la discussion',
  closeChat: 'Fermer la discussion',
  chatWithUs: 'Discutez avec nous',
  talkToSite: 'Parlez à notre site',
  chatDialogLabel: 'Discussion avec {name}',
  teamLabel: 'Équipe {name}',
  typing: "L'assistant écrit",
  tryAgain: 'Réessayer',
  reportIssue: 'Signaler le problème',
  reportSent: 'Merci, c’est enregistré.',
  reportNotSent: 'Le signalement n’a pas pu être envoyé.',
  statusPage: 'État du système',
  newChat: 'Nouvelle discussion',
  confirmNewChat: 'Confirmer la nouvelle discussion — la conversation sera effacée',
  tapAgainToClear: 'Appuyez à nouveau pour effacer cette conversation',
  tapAgainToClearShort: 'Appuyez à nouveau pour effacer',
  expandChat: 'Agrandir la discussion',
  collapseChat: 'Réduire la discussion',
  dismissSuggestions: 'Masquer les suggestions',
  bookAppointment: 'Prendre rendez-vous',
  anyLanguage: 'Écrivez dans la langue de votre choix, je réponds dans la vôtre.',
  typeAMessageIn: ['Type a message…', 'Scrivi un messaggio…', 'Schreiben Sie eine Nachricht…', 'Escribe un mensaje…'],
  languageButton: 'Langue : {language}. Changer de langue',
  languageMenuTitle: 'Langue des réponses',
  languageAutomatic: 'Automatique',
  languageAutomaticHint: 'Suit la langue dans laquelle vous écrivez',
  languageChosen: '{name} répondra en français.',
  languageAutomaticOn: '{name} répondra dans la langue dans laquelle vous écrivez.',
  leaveDetails: 'Laisser vos coordonnées',
  leadTitle: 'Laissez vos coordonnées et nous vous recontacterons.',
  leadSubmit: 'Envoyer',
  leadSending: 'Envoi en cours…',
  leadCancel: 'Annuler',
  leadThanks: 'Merci {name} ! Nous avons bien reçu vos coordonnées et vous recontacterons bientôt.',
  leadFirstNameRequired: 'Veuillez saisir votre prénom.',
  leadLastNameRequired: 'Veuillez saisir votre nom.',
  leadEmailInvalid: 'Veuillez saisir une adresse e-mail valide.',
  leadTooLong: 'Le texte est trop long, veuillez le raccourcir.',
  leadError: "Désolé, nous n'avons pas pu envoyer vos coordonnées. Veuillez réessayer.",
  leadCap: 'Nous recevons beaucoup de demandes en ce moment. Veuillez réessayer plus tard.',
  leadMessageLabel: 'Message',
  leadMessageRequired: 'Veuillez saisir un message.',
  detailsOptional: '{label} (facultatif)',
  leadFieldRequired: 'Veuillez remplir le champ « {label} ».',
  leadChoose: 'Choisir…',
  bookingDetailsTitle: 'Vos coordonnées pour la réservation',
  detailsFirstName: 'Prénom',
  detailsLastName: 'Nom',
  detailsEmail: 'E-mail',
  detailsPhone: 'Téléphone',
  bookingContinue: 'Continuer',
  bookingPhoneRequired: 'Veuillez saisir votre numéro de téléphone.',
  leadPhoneInvalid: 'Veuillez saisir un numéro de téléphone valide.',
  leadInvalid: 'Veuillez vérifier vos coordonnées et réessayer.',
  detailsCountry: 'Pays',
  nameTooLong: '{label} : {max} caractères maximum.',
  nameCharacters: '{label} ne peut contenir que des lettres, des espaces, des traits d’union, des apostrophes et des points.',
  nameNeedsLetter: '{label} doit contenir au moins une lettre.',
  nameWebAddress: '{label} ne peut pas être une adresse web.',
  emailRequired: 'Saisissez votre adresse e-mail.',
  emailNoAt: 'Une adresse e-mail contient un @, comme nom@exemple.fr.',
  emailSpaces: 'Une adresse e-mail ne peut pas contenir d’espaces.',
  emailDomain: 'Vérifiez la partie après le @ : elle doit ressembler à exemple.fr.',
  emailTooLong: 'L’adresse e-mail ne peut pas dépasser {max} caractères.',
  phoneTooLong: 'Ce numéro est trop long pour {country} : les numéros y ont {digits} chiffres après {dial}.',
  phoneTooShort: 'Ce numéro est trop court pour {country} : les numéros y ont {digits} chiffres après {dial}.',
  phoneWrongCountry: 'Ce numéro commence par {otherDial}, qui correspond à {other}. Choisissez {other} comme pays, ou saisissez un numéro pour {country}.',
  phoneUnknownCode: 'Ce numéro commence par {otherDial}, qui n’est pas un indicatif de pays.',
  phoneNotValid: 'Ce n’est pas un numéro de {country} : l’indicatif régional et le central commencent chacun par un chiffre de 2 à 9.',
  phoneStartsWith: 'Ce n’est pas un numéro de {country} : là-bas, aucun numéro ne commence par {startsWith} après l’indicatif {dial}.',
  textTooLong: '{label} peut contenir jusqu’à {max} caractères — celui-ci en a {count}.',
  selectRequired: 'Sélectionnez « {label} ».',
  leadFormChanged: 'Ce formulaire a changé depuis le chargement de la page : rechargez la page et réessayez.',
  bookingUnderName: 'Réservation au nom de {name}',
  bookingDetailsSent: 'Voici mes coordonnées',
  bookingConfirmYes: 'Oui, réserver',
  bookingConfirmNo: 'Non',
  bookingBooked: 'Réservation confirmée',
  bookingReference: 'Référence {id}',
  findBookingTitle: 'Retrouver votre réservation',
  findBookingReference: 'Référence de réservation',
  findBookingContact: 'E-mail ou téléphone utilisé pour réserver',
  findBookingReferenceInvalid: 'Saisissez la référence indiquée dans votre e-mail de confirmation.',
  findBookingContactInvalid: 'Saisissez une adresse e-mail ou un numéro de téléphone valide.',
  findBookingSubmit: 'Rechercher',
  findBookingLine: 'Référence {id}',
  bookingFound: 'Votre réservation',
  bookingCancelled: 'Réservation annulée',
  bookingChanged: 'Réservation modifiée',
  bookingWas: 'Avant : {when}',
  bookingConfirmCancelYes: 'Oui, annuler',
  bookingConfirmChangeYes: 'Oui, modifier',
  bookingConfirmKeep: 'Non, la garder',
  sendFeedback: 'Donner votre avis',
  backToChat: 'Retour à la discussion',
  feedbackSubtitle: 'Votre avis sur cet assistant',
  feedbackIntro: "Votre avis est transmis à Evrella, l'équipe qui conçoit cet assistant.",
  feedbackTypeLabel: "De quoi s'agit-il ?",
  feedbackTypeGeneral: 'Général',
  feedbackTypeFeature: 'Suggestion',
  feedbackTypeBug: 'Problème',
  feedbackTypeLove: 'Compliment',
  feedbackMessage: 'Votre avis',
  feedbackPlaceholder: "Ce qui a marché, ce qui n'a pas marché, ce qui vous aiderait ?",
  feedbackEmail: 'E-mail (facultatif, si vous souhaitez une réponse)',
  feedbackSubmit: 'Envoyer mon avis',
  feedbackMessageRequired: "Veuillez d'abord écrire votre avis.",
  feedbackThanks: 'Merci, votre avis a bien été envoyé.',
  feedbackError: "Désolé, nous n'avons pas pu envoyer votre avis. Veuillez réessayer.",
  humanCheckTitle: 'Vérification de sécurité',
  humanCheckFailed: "Désolé, nous n'avons pas pu vérifier que vous êtes une personne. Veuillez réessayer.",
};

const de: WidgetStrings = {
  subtitle: 'KI-Assistent',
  greeting: 'Hallo! Wie kann ich Ihnen helfen?',
  suggestions: ['Welche Leistungen bieten Sie an?', 'Preise anzeigen', 'Mehr erfahren'],
  errorMessage: 'Entschuldigung, die Verbindung ist gestört. Bitte erneut versuchen.',
  originBlockedMessage: 'Dieser Assistent ist auf dieser Website nicht verfügbar.',
  noContent: 'Ich habe noch keine Informationen, um zu antworten. Bitte schauen Sie bald wieder vorbei.',
  overCap: 'Dieser Assistent hat sein Nutzungslimit vorerst erreicht. Bitte versuchen Sie es später erneut.',
  unavailable: 'Entschuldigung, der Assistent ist gerade nicht verfügbar.',
  refused: 'Entschuldigung, bei dieser Anfrage kann ich nicht helfen.',
  aboutAssistant: 'Über diesen Assistenten',
  closeInfo: 'Schließen',
  assistantVersion: 'Evrella Assistant {version}',
  privacyNotice: 'Nachrichten werden gespeichert, um diesen Service bereitzustellen.',
  privacyLinkLabel: 'Datenschutzerklärung',
  privacyOperatorLinkLabel: 'Wie Ihre Nachrichten verarbeitet werden',
  typeAMessage: 'Nachricht schreiben...',
  sendMessage: 'Nachricht senden',
  openChat: 'Chat öffnen',
  closeChat: 'Chat schließen',
  chatWithUs: 'Schreiben Sie uns',
  talkToSite: 'Sprechen Sie mit unserer Website',
  chatDialogLabel: 'Chat mit {name}',
  teamLabel: 'Team {name}',
  typing: 'Der Assistent schreibt',
  tryAgain: 'Erneut versuchen',
  reportIssue: 'Problem melden',
  reportSent: 'Danke, wir haben es erfasst.',
  reportNotSent: 'Die Meldung konnte nicht gesendet werden.',
  statusPage: 'Systemstatus',
  newChat: 'Neuer Chat',
  confirmNewChat: 'Neuen Chat bestätigen — das Gespräch wird gelöscht',
  tapAgainToClear: 'Erneut tippen, um dieses Gespräch zu löschen',
  tapAgainToClearShort: 'Erneut tippen zum Löschen',
  expandChat: 'Chat vergrößern',
  collapseChat: 'Chat verkleinern',
  dismissSuggestions: 'Vorschläge ausblenden',
  bookAppointment: 'Termin buchen',
  anyLanguage: 'Schreiben Sie in jeder Sprache — ich antworte in Ihrer.',
  typeAMessageIn: ['Type a message…', 'Scrivi un messaggio…', 'Écrivez un message…', 'Escribe un mensaje…'],
  languageButton: 'Sprache: {language}. Sprache ändern',
  languageMenuTitle: 'Antwortsprache',
  languageAutomatic: 'Automatisch',
  languageAutomaticHint: 'Folgt der Sprache, in der Sie schreiben',
  languageChosen: '{name} antwortet ab jetzt auf Deutsch.',
  languageAutomaticOn: '{name} antwortet in der Sprache, in der Sie schreiben.',
  leaveDetails: 'Kontaktdaten hinterlassen',
  leadTitle: 'Hinterlassen Sie Ihre Kontaktdaten und wir melden uns bei Ihnen.',
  leadSubmit: 'Senden',
  leadSending: 'Wird gesendet…',
  leadCancel: 'Abbrechen',
  leadThanks: 'Danke, {name}! Wir haben Ihre Daten erhalten und melden uns bald.',
  leadFirstNameRequired: 'Bitte geben Sie Ihren Vornamen ein.',
  leadLastNameRequired: 'Bitte geben Sie Ihren Nachnamen ein.',
  leadEmailInvalid: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
  leadTooLong: 'Der Text ist zu lang – bitte kürzen.',
  leadError: 'Entschuldigung, Ihre Daten konnten nicht gesendet werden. Bitte erneut versuchen.',
  leadCap: 'Wir erhalten gerade sehr viele Anfragen. Bitte versuchen Sie es später erneut.',
  leadMessageLabel: 'Nachricht',
  leadMessageRequired: 'Bitte geben Sie eine Nachricht ein.',
  detailsOptional: '{label} (optional)',
  leadFieldRequired: 'Bitte füllen Sie „{label}“ aus.',
  leadChoose: 'Bitte wählen…',
  bookingDetailsTitle: 'Ihre Angaben für die Buchung',
  detailsFirstName: 'Vorname',
  detailsLastName: 'Nachname',
  detailsEmail: 'E-Mail',
  detailsPhone: 'Telefon',
  bookingContinue: 'Weiter',
  bookingPhoneRequired: 'Bitte geben Sie Ihre Telefonnummer ein.',
  leadPhoneInvalid: 'Bitte geben Sie eine gültige Telefonnummer ein.',
  leadInvalid: 'Bitte prüfen Sie Ihre Angaben und versuchen Sie es erneut.',
  detailsCountry: 'Land',
  nameTooLong: '{label}: höchstens {max} Zeichen.',
  nameCharacters: '{label} darf nur Buchstaben, Leerzeichen, Bindestriche, Apostrophe und Punkte enthalten.',
  nameNeedsLetter: '{label} muss mindestens einen Buchstaben enthalten.',
  nameWebAddress: '{label} darf keine Webadresse sein.',
  emailRequired: 'Geben Sie Ihre E-Mail-Adresse ein.',
  emailNoAt: 'Eine E-Mail-Adresse braucht ein @, z. B. name@beispiel.de.',
  emailSpaces: 'E-Mail-Adressen dürfen keine Leerzeichen enthalten.',
  emailDomain: 'Prüfen Sie den Teil nach dem @ – er sollte wie beispiel.de aussehen.',
  emailTooLong: 'Die E-Mail-Adresse darf höchstens {max} Zeichen lang sein.',
  phoneTooLong: 'Die Nummer ist zu lang für {country} – Nummern dort haben {digits} Ziffern nach {dial}.',
  phoneTooShort: 'Die Nummer ist zu kurz für {country} – Nummern dort haben {digits} Ziffern nach {dial}.',
  phoneWrongCountry: 'Die Nummer beginnt mit {otherDial}, das ist {other}. Wählen Sie {other} als Land oder geben Sie eine Nummer für {country} ein.',
  phoneUnknownCode: 'Die Nummer beginnt mit {otherDial}, das ist keine Ländervorwahl.',
  phoneNotValid: 'Das ist keine Nummer in {country} – Vorwahl und Rufnummer beginnen jeweils mit 2 bis 9.',
  phoneStartsWith: 'Das ist keine Nummer in {country} – dort beginnt keine Nummer nach {dial} mit {startsWith}.',
  textTooLong: '{label} darf bis zu {max} Zeichen lang sein – dieser Text hat {count}.',
  selectRequired: 'Wählen Sie „{label}“ aus.',
  leadFormChanged: 'Dieses Formular hat sich seit dem Laden der Seite geändert – bitte laden Sie die Seite neu und versuchen Sie es erneut.',
  bookingUnderName: 'Buchung auf den Namen {name}',
  bookingDetailsSent: 'Hier sind meine Angaben',
  bookingConfirmYes: 'Ja, buchen',
  bookingConfirmNo: 'Nein',
  bookingBooked: 'Buchung bestätigt',
  bookingReference: 'Referenz {id}',
  findBookingTitle: 'Ihre Buchung finden',
  findBookingReference: 'Buchungsreferenz',
  findBookingContact: 'E-Mail oder Telefon, mit dem Sie gebucht haben',
  findBookingReferenceInvalid: 'Bitte geben Sie die Referenz aus Ihrer Bestätigungs-E-Mail ein.',
  findBookingContactInvalid: 'Bitte geben Sie eine gültige E-Mail-Adresse oder Telefonnummer ein.',
  findBookingSubmit: 'Suchen',
  findBookingLine: 'Buchungsreferenz {id}',
  bookingFound: 'Ihre Buchung',
  bookingCancelled: 'Buchung storniert',
  bookingChanged: 'Buchung geändert',
  bookingWas: 'Vorher: {when}',
  bookingConfirmCancelYes: 'Ja, stornieren',
  bookingConfirmChangeYes: 'Ja, ändern',
  bookingConfirmKeep: 'Nein, behalten',
  sendFeedback: 'Feedback senden',
  backToChat: 'Zurück zum Chat',
  feedbackSubtitle: 'Feedback zu diesem Assistenten',
  feedbackIntro: 'Ihr Feedback geht an Evrella, das Team hinter diesem Assistenten.',
  feedbackTypeLabel: 'Worum geht es?',
  feedbackTypeGeneral: 'Allgemein',
  feedbackTypeFeature: 'Vorschlag',
  feedbackTypeBug: 'Problem',
  feedbackTypeLove: 'Lob',
  feedbackMessage: 'Ihr Feedback',
  feedbackPlaceholder: 'Was hat funktioniert, was nicht, was würde helfen?',
  feedbackEmail: 'E-Mail (optional, falls Sie eine Antwort möchten)',
  feedbackSubmit: 'Feedback senden',
  feedbackMessageRequired: 'Bitte schreiben Sie zuerst Ihr Feedback.',
  feedbackThanks: 'Danke, Ihr Feedback wurde gesendet.',
  feedbackError: 'Entschuldigung, Ihr Feedback konnte nicht gesendet werden. Bitte erneut versuchen.',
  humanCheckTitle: 'Sicherheitsprüfung',
  humanCheckFailed: 'Entschuldigung, wir konnten nicht bestätigen, dass Sie ein Mensch sind. Bitte erneut versuchen.',
};

const es: WidgetStrings = {
  subtitle: 'Asistente de IA',
  greeting: '¡Hola! ¿En qué puedo ayudarte?',
  suggestions: ['¿Qué servicios ofrecen?', 'Ver precios', 'Cuéntame más'],
  errorMessage: 'Lo siento, tengo problemas de conexión. Inténtalo de nuevo.',
  originBlockedMessage: 'Este asistente no está disponible en este sitio.',
  noContent: 'Todavía no tengo información para responder. Vuelve a intentarlo pronto.',
  overCap: 'Este asistente ha alcanzado su límite de uso por ahora. Inténtalo de nuevo más tarde.',
  unavailable: 'Lo siento, el asistente no está disponible en este momento.',
  refused: 'Lo siento, no puedo ayudarte con esa solicitud.',
  aboutAssistant: 'Acerca de este asistente',
  closeInfo: 'Cerrar',
  assistantVersion: 'Evrella Assistant {version}',
  privacyNotice: 'Los mensajes se guardan para prestar este servicio.',
  privacyLinkLabel: 'Política de privacidad',
  privacyOperatorLinkLabel: 'Cómo se tratan tus mensajes',
  typeAMessage: 'Escribe un mensaje...',
  sendMessage: 'Enviar el mensaje',
  openChat: 'Abrir el chat',
  closeChat: 'Cerrar el chat',
  chatWithUs: 'Escríbenos',
  talkToSite: 'Habla con nuestra web',
  chatDialogLabel: 'Chat de {name}',
  teamLabel: 'Equipo de {name}',
  typing: 'El asistente está escribiendo',
  tryAgain: 'Reintentar',
  reportIssue: 'Informar del problema',
  reportSent: 'Gracias, lo hemos registrado.',
  reportNotSent: 'No se pudo enviar el aviso.',
  statusPage: 'Estado del sistema',
  newChat: 'Nuevo chat',
  confirmNewChat: 'Confirmar el nuevo chat — se borrará la conversación',
  tapAgainToClear: 'Toca de nuevo para borrar esta conversación',
  tapAgainToClearShort: 'Toca de nuevo para borrar',
  expandChat: 'Ampliar el chat',
  collapseChat: 'Reducir el chat',
  dismissSuggestions: 'Ocultar las sugerencias',
  bookAppointment: 'Reservar una cita',
  anyLanguage: 'Escribe en cualquier idioma: te respondo en el tuyo.',
  typeAMessageIn: ['Type a message…', 'Scrivi un messaggio…', 'Écrivez un message…', 'Schreiben Sie eine Nachricht…'],
  languageButton: 'Idioma: {language}. Cambiar idioma',
  languageMenuTitle: 'Idioma de las respuestas',
  languageAutomatic: 'Automático',
  languageAutomaticHint: 'Sigue el idioma en el que escribes',
  languageChosen: '{name} responderá en español.',
  languageAutomaticOn: '{name} responderá en el idioma en el que escribas.',
  leaveDetails: 'Deja tus datos',
  leadTitle: 'Déjanos tus datos y nos pondremos en contacto contigo.',
  leadSubmit: 'Enviar',
  leadSending: 'Enviando…',
  leadCancel: 'Cancelar',
  leadThanks: '¡Gracias, {name}! Hemos recibido tus datos y te contactaremos pronto.',
  leadFirstNameRequired: 'Introduce tu nombre.',
  leadLastNameRequired: 'Introduce tus apellidos.',
  leadEmailInvalid: 'Introduce una dirección de correo válida.',
  leadTooLong: 'El texto es demasiado largo; acórtalo, por favor.',
  leadError: 'Lo siento, no hemos podido enviar tus datos. Inténtalo de nuevo.',
  leadCap: 'Estamos recibiendo muchas consultas ahora mismo. Inténtalo de nuevo más tarde.',
  leadMessageLabel: 'Mensaje',
  leadMessageRequired: 'Escribe un mensaje.',
  detailsOptional: '{label} (opcional)',
  leadFieldRequired: 'Rellena el campo «{label}».',
  leadChoose: 'Elige…',
  bookingDetailsTitle: 'Tus datos para la reserva',
  detailsFirstName: 'Nombre',
  detailsLastName: 'Apellidos',
  detailsEmail: 'Correo electrónico',
  detailsPhone: 'Teléfono',
  bookingContinue: 'Continuar',
  bookingPhoneRequired: 'Introduce tu número de teléfono.',
  leadPhoneInvalid: 'Introduce un número de teléfono válido.',
  leadInvalid: 'Revisa tus datos e inténtalo de nuevo.',
  detailsCountry: 'País',
  nameTooLong: '{label}: máximo {max} caracteres.',
  nameCharacters: '{label} solo puede contener letras, espacios, guiones, apóstrofos y puntos.',
  nameNeedsLetter: '{label} debe contener al menos una letra.',
  nameWebAddress: '{label} no puede ser una dirección web.',
  emailRequired: 'Introduce tu correo electrónico.',
  emailNoAt: 'Un correo electrónico necesita una @, como nombre@ejemplo.es.',
  emailSpaces: 'Un correo electrónico no puede contener espacios.',
  emailDomain: 'Revisa la parte después de la @: debería ser como ejemplo.es.',
  emailTooLong: 'El correo electrónico puede tener como máximo {max} caracteres.',
  phoneTooLong: 'El número es demasiado largo para {country}: allí los números tienen {digits} dígitos después de {dial}.',
  phoneTooShort: 'El número es demasiado corto para {country}: allí los números tienen {digits} dígitos después de {dial}.',
  phoneWrongCountry: 'El número empieza por {otherDial}, que es {other}. Elige {other} como país o introduce un número de {country}.',
  phoneUnknownCode: 'El número empieza por {otherDial}, que no es un prefijo de país.',
  phoneNotValid: 'No es un número de {country}: el prefijo de zona y la central empiezan por una cifra del 2 al 9.',
  phoneStartsWith: 'No es un número de {country}: allí ningún número empieza por {startsWith} después del prefijo {dial}.',
  textTooLong: '{label} puede tener hasta {max} caracteres; este tiene {count}.',
  selectRequired: 'Selecciona «{label}».',
  leadFormChanged: 'Este formulario ha cambiado desde que se cargó la página: recarga la página y vuelve a intentarlo.',
  bookingUnderName: 'Reserva a nombre de {name}',
  bookingDetailsSent: 'Aquí tienes mis datos',
  bookingConfirmYes: 'Sí, reservar',
  bookingConfirmNo: 'No',
  bookingBooked: 'Reserva confirmada',
  bookingReference: 'Referencia {id}',
  findBookingTitle: 'Encuentra tu reserva',
  findBookingReference: 'Referencia de la reserva',
  findBookingContact: 'Correo o teléfono con el que reservaste',
  findBookingReferenceInvalid: 'Introduce la referencia de tu correo de confirmación.',
  findBookingContactInvalid: 'Introduce un correo electrónico o un teléfono válido.',
  findBookingSubmit: 'Buscar',
  findBookingLine: 'Referencia {id}',
  bookingFound: 'Tu reserva',
  bookingCancelled: 'Reserva cancelada',
  bookingChanged: 'Reserva modificada',
  bookingWas: 'Antes: {when}',
  bookingConfirmCancelYes: 'Sí, cancelar',
  bookingConfirmChangeYes: 'Sí, cambiar',
  bookingConfirmKeep: 'No, mantenerla',
  sendFeedback: 'Enviar comentarios',
  backToChat: 'Volver al chat',
  feedbackSubtitle: 'Comentarios sobre este asistente',
  feedbackIntro: 'Tus comentarios llegan a Evrella, el equipo que crea este asistente.',
  feedbackTypeLabel: '¿De qué se trata?',
  feedbackTypeGeneral: 'General',
  feedbackTypeFeature: 'Sugerencia',
  feedbackTypeBug: 'Problema',
  feedbackTypeLove: 'Elogio',
  feedbackMessage: 'Tus comentarios',
  feedbackPlaceholder: '¿Qué ha funcionado, qué no y qué te ayudaría?',
  feedbackEmail: 'Correo electrónico (opcional, si quieres respuesta)',
  feedbackSubmit: 'Enviar comentarios',
  feedbackMessageRequired: 'Escribe primero tus comentarios.',
  feedbackThanks: 'Gracias, tus comentarios se han enviado.',
  feedbackError: 'Lo siento, no hemos podido enviar tus comentarios. Inténtalo de nuevo.',
  humanCheckTitle: 'Comprobación de seguridad',
  humanCheckFailed: 'Lo siento, no hemos podido confirmar que eres una persona. Inténtalo de nuevo.',
};

const STRINGS: Record<string, WidgetStrings> = { en, it, fr, de, es };

/**
 * The page's language, as the page itself declares it.
 *
 * `<html lang>` first: it is what the site AUTHOR said the page is written in,
 * and on a site with a language switcher it is the only thing that moves when
 * the visitor switches. `navigator.language` is the reader's preference, not
 * the page's, so it is the fallback rather than the source — a German browser
 * on an Italian page is reading Italian.
 */
export function detectLanguage(): string {
  if (typeof document !== 'undefined') {
    const declared = document.documentElement.getAttribute('lang');
    if (declared && declared.trim()) return declared.trim();
  }
  if (typeof navigator !== 'undefined' && navigator.language) return navigator.language;
  return 'en';
}

/** Copy for a language tag ('it', 'it-IT', 'IT'), English when unreviewed. */
export function stringsFor(tag: string): WidgetStrings {
  return STRINGS[tag.split('-')[0].toLowerCase()] ?? en;
}

/** `fill('{name} team', { name: 'Acme' })` → `'Acme team'`. Unknown keys are left as they are. */
export function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) =>
    Object.prototype.hasOwnProperty.call(values, key) ? values[key] : whole,
  );
}
